/*
 * This module's entire job is to take ownership of the global `console`, so it necessarily names every
 * console method — including the ones `no-console` exists to keep out of application code. The rule is
 * disabled here and NOWHERE else: any other file reaching for `console.log` should still be stopped.
 */
/* eslint-disable no-console */
/**
 * Routes every line the dev process emits into the per-service log files, so the terminal can be given
 * over entirely to the status panel.
 *
 * ## Why this has to exist
 *
 * The backend runs IN-PROCESS: `ServerlessLocalRun` is fastify + `@aws-lambda-powertools/logger` in the
 * same node process, and the runner sets `POWERTOOLS_DEV=true`, which makes Powertools bind the GLOBAL
 * `console`. So a handler's `console.log` AND every Powertools line reach `process.stdout` through
 * channels no seam of ours owns. There is no other place to catch them. (Reassigning the Powertools
 * `Logger`'s console does NOT work: handlers are given a `createChild()` logger, and `createChild`
 * re-derives its console from the global — the parent's is never consulted.)
 *
 * ## Why this is not the classifier that was rejected
 *
 * An earlier design tried to decide, per line, whether to PRINT it — inferring "whatever is left on
 * stdout must be human, therefore promote it". That was unsound: the residual bucket also holds the
 * env-gated raw request line, dependency import banners, and Node's own warnings, so the rule promoted
 * exactly the noise it meant to hide. There is no such decision here. **Every line goes to a file** —
 * one rule, no residue, nothing to be wrong about. The only thing read is DECLARED provenance: which
 * `console` method the caller chose, and which fd it wrote to. The line's bytes are never inspected.
 *
 * ## Two switches, not one
 *
 * The file sink installs EARLY (at process start) in `tee` mode: lines go to the file AND the terminal,
 * exactly as before. Terminal suppression flips separately, at `ready()`. That split matters — handler
 * modules are imported during boot, long before the panel exists, so a single late install would let
 * their import-time output escape uncaptured; and a single early install would swallow a boot crash,
 * leaving the user with a blank screen and no stack.
 *
 * ## Anti-recursion
 *
 * The panel writes to the terminal through {@link rawStdoutWrite}, which resolves `write` off the
 * PROTOTYPE and so steps over the own-property patch installed here. The interceptor's only sink is a
 * file. The failure mode to guard against is not a stack overflow — it is a BLACK HOLE: hand the panel
 * a patched stream and its frames are quietly filed into a log instead of drawn.
 */
import process from 'node:process'
import util from 'node:util'

import type { DevLogSink } from './log-sink.js'
import { rawStderrWrite, rawStdoutWrite } from './log-sink.js'
import type { LogLevel } from './render.js'

/** The `console` methods that reach a terminal, each with the level its NAME declares. */
const CONSOLE_LEVELS = {
  log: 'info',
  info: 'info',
  debug: 'debug',
  trace: 'debug',
  warn: 'warn',
  error: 'error',
} as const satisfies Record<string, LogLevel>

type ConsoleMethod = keyof typeof CONSOLE_LEVELS

export interface OutputInterceptOptions {
  /** Where every captured line is filed. */
  sink: DevLogSink
  /** The service to file a line under when nothing else claims it. A NAMED bucket, never a guess. */
  fallbackService: string
  /** The service owning the current async context, if any (the `AsyncLocalStorage` lookup). */
  currentService: () => string | undefined
  /**
   * The terminal seam used to echo lines while in tee mode. Defaults to {@link rawStdoutWrite} — the
   * prototype bypass, which is the ONLY way to reach the screen once this interceptor owns
   * `process.stdout`. Injected so tests can observe it: by design it cannot be spied on, because it
   * deliberately holds the original function rather than resolving `write` off the (patched) stream.
   */
  terminalWrite?: (text: string) => void
  /** The stderr counterpart of {@link terminalWrite}. Same reason, different fd. */
  stderrWrite?: (text: string) => void
}

export interface OutputIntercept {
  /**
   * Stop echoing captured lines to the terminal; from now on they only reach their file. Called at
   * `ready()`, once the panel owns the screen. Idempotent.
   */
  suppressTerminal: () => void
  /** Restore `console` and the raw stream writes to exactly what they were. Idempotent. */
  uninstall: () => void
}

/**
 * Install the interception. Returns the two switches; call {@link OutputIntercept.uninstall} on every
 * exit path, BEFORE anything else prints.
 *
 * Never install this on a non-TTY / `--json` / MCP run: it would file the machine-readable stream into
 * a log and hand the caller an empty stdout. The caller owns that gate.
 */
export const installOutputIntercept = ({
  sink,
  fallbackService,
  currentService,
  terminalWrite = rawStdoutWrite,
  stderrWrite = rawStderrWrite,
}: OutputInterceptOptions): OutputIntercept => {
  let suppressed = false
  let live = true

  const serviceFor = (): string => {
    return currentService() ?? fallbackService
  }

  /** File a line, and echo it to the real terminal while still in tee mode. */
  const capture = (text: string, level: LogLevel): void => {
    sink.write(serviceFor(), text, { level })
    if (!suppressed) terminalWrite(text.endsWith('\n') ? text : `${text}\n`)
  }

  // ---- console ------------------------------------------------------------
  //
  // The console patches do NOT delegate to the original methods. They must not: `console.log` writes
  // through `process.stdout.write`, which is patched below, so delegating would file every console line
  // TWICE and double every counter. Routing them straight to the sink also preserves the one thing the
  // raw-stream patch cannot see — the level the caller DECLARED by picking `warn` over `log`.
  const originalConsole = {} as Record<ConsoleMethod, (...args: unknown[]) => void>

  for (const method of Object.keys(CONSOLE_LEVELS) as ConsoleMethod[]) {
    originalConsole[method] = console[method] as (...args: unknown[]) => void
    console[method] = (...args: unknown[]): void => {
      if (!live) {
        originalConsole[method](...args)

        return
      }
      capture(util.format(...args), CONSOLE_LEVELS[method])
    }
  }

  // ---- raw stream writes --------------------------------------------------
  //
  // What is left after the console patch: writes that bypass `console` entirely — the env-gated raw
  // request line, a dependency's import-time banner, and `process.emitWarning` (which goes to stderr,
  // NOT through `console.warn`). Chunk-oriented, so they are line-buffered before being filed.
  const patchStream = (stream: NodeJS.WriteStream, level: LogLevel, echo: (text: string) => void): (() => void) => {
    const original = stream.write.bind(stream)
    let pending = ''

    const flush = (): void => {
      if (pending === '') return
      sink.write(serviceFor(), pending, { level })
      pending = ''
    }

    stream.write = ((chunk: unknown, ...rest: unknown[]): boolean => {
      if (!live || typeof chunk !== 'string') {
        return (original as (...args: unknown[]) => boolean)(chunk, ...rest)
      }

      if (!suppressed) echo(chunk)

      pending += chunk

      const lines = pending.split('\n')

      pending = lines.pop() ?? ''
      for (const line of lines) {
        if (line !== '') sink.write(serviceFor(), line, { level })
      }

      // A chunk that never ends in a newline (a progress line, a prompt) would otherwise be held
      // forever; flush it once it is clearly not a partial line.
      if (pending.length > 8192) flush()

      // Honour the stream contract's completion callback — `write(chunk, cb)` and `write(chunk, enc, cb)`
      // both promise to call it. Swallowing it hangs any caller that awaits the write before proceeding
      // (a logger flushing before exit, a promisified write), and it would hang inside a dev session
      // whose terminal shows nothing but a frozen panel. Deferred, exactly as a real stream defers it.
      const callback = rest.find((arg) => {
        return typeof arg === 'function'
      })

      if (typeof callback === 'function') {
        process.nextTick(callback as () => void)
      }

      return true
    }) as NodeJS.WriteStream['write']

    return (): void => {
      flush()
      // Delete the own-property patch rather than reassigning the original: reassigning would leave a
      // second own property in place, and `hasForeignStdoutPatch` (and the next installer) would still
      // see a patched stream.
      Reflect.deleteProperty(stream, 'write')
    }
  }

  // Each stream echoes to its OWN fd. Sending stderr's tee through stdout would mean `dev 2>boot.log`
  // put stderr on the terminal and left the file empty — silently moving output the caller redirected.
  //
  // stderr is levelled `warn`, not `error`: `process.emitWarning` is far and away its highest-volume
  // user, and a genuine fault does not rely on this level — the crash barrier reports through
  // `DevServerRunner.reportFault`, which files at `error` AND punches the stack onto the terminal.
  const restoreStdout = patchStream(process.stdout, 'info', terminalWrite)
  const restoreStderr = patchStream(process.stderr, 'warn', stderrWrite)

  return {
    suppressTerminal: (): void => {
      suppressed = true
    },
    uninstall: (): void => {
      if (!live) return
      live = false
      restoreStdout()
      restoreStderr()
      for (const method of Object.keys(CONSOLE_LEVELS) as ConsoleMethod[]) {
        console[method] = originalConsole[method] as typeof console.log
      }
    },
  }
}
